import React, { useEffect, useState, useCallback } from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Breadcrumbs as MuiBreadcrumbs,
  Checkbox,
  Divider as MuiDivider,
  FormControlLabel,
  Grid,
  Link,
  Paper as MuiPaper,
  TablePagination,
  TablePaginationProps,
  TextField,
  Typography,
  Button as MuiButton,
  MenuItem,
  Select,
  CircularProgress,
} from "@mui/material";
import { Box, spacing } from "@mui/system";
import agent from "../../utils/axios";
import LoadingSpinner from "../Loading/LoadingSpinner";
import StagingItem from "./StagingItem";
import {
  PayeeModal,
  TinMatchModal,
  PayeeDetailModal,
  BillDetailModal,
} from "./Modals";

// Styled components
const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Paper = styled(MuiPaper)(spacing);
const Button = styled(MuiButton)`
  border-radius: 8px;
  text-transform: none;
  font-weight: 500;
`;

interface CSITipaltiStagings {
  id: string;
  payeeId: string;
  tin: string;
  groupPayeeIds: string[];
  matchedPayeeId: string | null;
  additionalMatchedPayeeIds: string[];
  csiPaymentMethod: string;
  csiCreatedDate: string;
  csiName: string;
  csiEmail: string;
  csiAddress: string;
  tipaltiName: string;
  tipaltiEmail: string;
  tipaltiAddress: string;
  tipaltiPaymentMethod: string;
  inTipalti: boolean | null;
  isMatchedWithTipalti: boolean | null;
  matchBasis: string;
  migratedBills: number;
  nonMigratedBills: number;
  analysisCreatedOn: string;
  analysisCompletedOn: string | null;
  analysisCompletedBy: string;
  isPayable: boolean | null;
  payableReason: string;
  memo: string | null;
  lastModified: string | null;
  lastModifiedBy: string | null;
  reviewedResults: string | null;
  isActive: boolean;
}

function CSITipaltiStagingList() {
  const [loading, setLoading] = useState(true);
  const [massUpdateLoading, setMassUpdateLoading] = useState(false);
  const [data, setData] = useState<CSITipaltiStagings[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedPayeeItem, setSelectedPayeeItem] =
    useState<CSITipaltiStagings | null>(null);
  const [tinMatchModalTin, setTinMatchModalTin] = useState<string | null>(null);
  const [payeeDetailModalId, setPayeeDetailModalId] = useState<string | null>(
    null
  );
  const [billDetailModalRefCode, setBillDetailModalRefCode] = useState<
    string | null
  >(null);
  const [filterInTipalti, setFilterInTipalti] = useState<boolean | null>(null);
  const [filterMatched, setFilterMatched] = useState<boolean | null>(null);
  const [filterPayable, setFilterPayable] = useState<boolean | null>(null);
  const [filterActive, setFilterActive] = useState<boolean | null>(true);
  const [filterGroupsMode, setFilterGroupsMode] = useState<
    "all" | "single" | "multiple"
  >("all");
  const [filterMigratedBills, setFilterMigratedBills] = useState<
    boolean | null
  >(null);
  const [filterNonMigratedBills, setFilterNonMigratedBills] = useState<
    boolean | null
  >(null);
  const [filterCsiPaymentMethod, setFilterCsiPaymentMethod] =
    useState<string>("all");
  const [filterReviewedResults, setFilterReviewedResults] =
    useState<string>("all");
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [massMemo, setMassMemo] = useState("");
  const [massReviewedResults, setMassReviewedResults] = useState("");

  // Reset all filters to default values
  const resetFilters = () => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setFilterInTipalti(null);
    setFilterMatched(null);
    setFilterPayable(null);
    setFilterActive(true);
    setFilterGroupsMode("all");
    setFilterMigratedBills(null);
    setFilterNonMigratedBills(null);
    setFilterCsiPaymentMethod("all");
    setFilterReviewedResults("all");
    setSelectedItems([]);
    setSelectAll(false);
    setMassMemo("");
    setMassReviewedResults("");
    setPage(0);
  };

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const fetchStagingData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const effectivePageSize = rowsPerPage === -1 ? 100000 : rowsPerPage;

      // Debug: Log the filter values being sent
      console.log("Filter values being sent:", {
        page: page + 1,
        rowsPerPage,
        filterActive,
        debouncedSearchTerm,
        filterInTipalti,
        filterMatched,
        filterPayable,
        filterGroupsMode,
      });

      const response = await agent.CSITipalti.getAllAnalysis(
        page + 1,
        rowsPerPage,
        filterActive,
        debouncedSearchTerm,
        "CsiCreatedDate",
        "desc",
        filterInTipalti,
        filterMatched,
        filterPayable,
        filterGroupsMode,
        filterMigratedBills,
        filterNonMigratedBills,
        filterCsiPaymentMethod,
        filterReviewedResults
      );

      // response is a Pagination<T> coming from the controller:
      // { pageIndex, pageSize, count, data }
      const paged = response; // if axios agent already returns res.data
      const items = Array.isArray(paged?.data)
        ? paged.data
        : Array.isArray(response?.data)
        ? response.data
        : [];

      const sanitizedData = items.map((item: any) => ({
        id: item.id || "", // ✅ correct id
        payeeId: item.payeeId || "",
        tin: item.tin || "",
        groupPayeeIds: Array.isArray(item.groupPayeeIds)
          ? item.groupPayeeIds
          : [],
        matchedPayeeId: item.matchedPayeeId || null,
        additionalMatchedPayeeIds: Array.isArray(item.additionalMatchedPayeeIds)
          ? item.additionalMatchedPayeeIds
          : [],
        csiPaymentMethod: item.csiPaymentMethod || "",
        csiCreatedDate: item.csiCreatedDate || new Date().toISOString(),
        csiName: item.csiName || "",
        csiEmail: item.csiEmail || "",
        csiAddress: item.csiAddress || "",
        tipaltiName: item.tipaltiName || "",
        tipaltiEmail: item.tipaltiEmail || "",
        tipaltiAddress: item.tipaltiAddress || "",
        tipaltiPaymentMethod: item.tipaltiPaymentMethod || "",
        inTipalti: typeof item.inTipalti === "boolean" ? item.inTipalti : null,
        isMatchedWithTipalti:
          typeof item.isMatchedWithTipalti === "boolean"
            ? item.isMatchedWithTipalti
            : null,
        matchBasis: item.matchBasis || "",
        migratedBills: Number.isFinite(item.migratedBills)
          ? item.migratedBills
          : 0,
        nonMigratedBills: Number.isFinite(item.nonMigratedBills)
          ? item.nonMigratedBills
          : 0,
        analysisCreatedOn: item.analysisCreatedOn || new Date().toISOString(),
        analysisCompletedOn: item.analysisCompletedOn || null,
        analysisCompletedBy: item.analysisCompletedBy || "",
        isPayable: typeof item.isPayable === "boolean" ? item.isPayable : null,
        payableReason: item.payableReason || "",
        memo: item.memo ?? null,
        lastModified: item.lastModified || null,
        lastModifiedBy: item.lastModifiedBy || null,
        reviewedResults: item.reviewedResults || null,
        isActive: typeof item.isActive === "boolean" ? item.isActive : true, // will respect server once DTO has it
      }));

      setData(sanitizedData);
      setTotalCount(paged?.count ?? paged?.totalCount ?? sanitizedData.length);
    } catch (error: any) {
      console.error("Error fetching staging data:", error);
      setError("Failed to load data. Please check the server and try again.");
    } finally {
      setLoading(false);
    }
  }, [
    page,
    rowsPerPage,
    filterActive,
    debouncedSearchTerm,
    filterInTipalti,
    filterMatched,
    filterPayable,
    filterGroupsMode,
    filterMigratedBills,
    filterNonMigratedBills,
    filterCsiPaymentMethod,
    filterReviewedResults,
  ]);

  useEffect(() => {
    fetchStagingData();
  }, [fetchStagingData]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage: TablePaginationProps["onRowsPerPageChange"] = (
    event
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleTinClick = (tin: string) => {
    setTinMatchModalTin(tin);
  };

  const handlePayeeIdClick = (payeeId: string) => {
    const foundItem = data.find((item) => item.payeeId === payeeId);
    setSelectedPayeeItem(foundItem || null);
  };

  const handleMainPayeeIdClick = (payeeId: string) => {
    setPayeeDetailModalId(payeeId);
  };

  const handleBillRefCodeClick = (billRefCode: string) => {
    setBillDetailModalRefCode(billRefCode);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedItems([]);
    } else {
      setSelectedItems(data.map((item) => item.id));
    }
    setSelectAll(!selectAll);
  };

  const handleMassUpdateActive = async (isActive: boolean) => {
    if (massMemo.length < 7) {
      setError("Memo must be at least 7 characters long for mass updates.");
      return;
    }

    if (!massReviewedResults) {
      setError("Review status must be selected for mass updates.");
      return;
    }

    setMassUpdateLoading(true);
    setError(null);

    try {
      const now = new Date().toISOString();
      for (const id of selectedItems) {
        const item = data.find((d) => d.id === id);
        if (item) {
          const updatedItem = {
            ...item,
            isActive,
            memo: massMemo,
            reviewedResults: massReviewedResults,
            lastModified: now,
            lastModifiedBy: "currentUser", // Replace with actual user
          };
          await agent.CSITipalti.updateCSITipalti(id, updatedItem);
        }
      }
      await fetchStagingData();
      setSelectedItems([]);
      setSelectAll(false);
      setMassMemo("");
      setMassReviewedResults("");
    } catch (error) {
      console.error("Error updating active status:", error);
      setError("Failed to update active status. Please try again.");
    } finally {
      setMassUpdateLoading(false);
    }
  };

  const rowsPerPageOptions = [
    10,
    25,
    50,
    100,
    500,
    1000,
    { label: "All", value: -1 },
  ];

  return (
    <Paper
      m={4}
      p={4}
      sx={{ borderRadius: "12px", boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)" }}
    >
      <Helmet title="CSITipaltiStagings" />
      <Grid justifyContent="space-between" container spacing={2}>
        <Grid item>
          <Typography variant="h4" gutterBottom>
            CSI Tipalti Analysis
          </Typography>
          <Breadcrumbs aria-label="Breadcrumb" mb={2}>
            <Link component={NavLink} to="/" color="primary">
              Dashboard
            </Link>
            <Typography color="text.primary">CSI Tipalti Analysis</Typography>
          </Breadcrumbs>
        </Grid>
      </Grid>
      <Box mb={4}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <TextField
              label="Search by Name, Payee ID, or Email"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Enter name, payee ID, or email..."
              aria-label="Search payees"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filterInTipalti === true}
                    indeterminate={filterInTipalti === null}
                    onChange={() => {
                      setFilterInTipalti(
                        filterInTipalti === null
                          ? true
                          : filterInTipalti === true
                          ? false
                          : null
                      );
                    }}
                  />
                }
                label="In Tipalti"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filterMatched === true}
                    indeterminate={filterMatched === null}
                    onChange={() => {
                      setFilterMatched(
                        filterMatched === null
                          ? true
                          : filterMatched === true
                          ? false
                          : null
                      );
                    }}
                  />
                }
                label="Matched"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filterPayable === true}
                    indeterminate={filterPayable === null}
                    onChange={() => {
                      setFilterPayable(
                        filterPayable === null
                          ? true
                          : filterPayable === true
                          ? false
                          : null
                      );
                    }}
                  />
                }
                label="Payable"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filterMigratedBills === true}
                    indeterminate={filterMigratedBills === null}
                    onChange={() => {
                      setFilterMigratedBills(
                        filterMigratedBills === null
                          ? true
                          : filterMigratedBills === true
                          ? false
                          : null
                      );
                    }}
                  />
                }
                label="Has Migrated Bills"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filterNonMigratedBills === true}
                    indeterminate={filterNonMigratedBills === null}
                    onChange={() => {
                      setFilterNonMigratedBills(
                        filterNonMigratedBills === null
                          ? true
                          : filterNonMigratedBills === true
                          ? false
                          : null
                      );
                    }}
                  />
                }
                label="Has Non-Migrated Bills"
              />
              <TextField
                select
                label="Group Filter"
                value={filterGroupsMode}
                onChange={(e) =>
                  setFilterGroupsMode(
                    e.target.value as "all" | "single" | "multiple"
                  )
                }
                SelectProps={{
                  native: true,
                }}
                variant="outlined"
                size="small"
              >
                <option value="all">All Groups</option>
                <option value="single">Single Groups</option>
                <option value="multiple">Multiple Groups</option>
              </TextField>
              <TextField
                select
                label="Payment Method"
                value={filterCsiPaymentMethod}
                onChange={(e) => setFilterCsiPaymentMethod(e.target.value)}
                SelectProps={{
                  native: true,
                }}
                variant="outlined"
                size="small"
              >
                <option value="all">All Methods</option>
                <option value="ach">ACH</option>
                <option value="check">Check</option>
                <option value="other">Other</option>
              </TextField>
              <TextField
                select
                label="Reviewed Results"
                value={filterReviewedResults}
                onChange={(e) => setFilterReviewedResults(e.target.value)}
                SelectProps={{
                  native: true,
                }}
                variant="outlined"
                size="small"
              >
                <option value="all">All Results</option>
                <option value="none">No Results</option>
                <option value="No Further Action">No Further Action</option>
                <option value="Need Additional Information">
                  Need Additional Information
                </option>
                <option value="Waiting on Applicant">
                  Waiting on Applicant
                </option>
                <option value="Duplicate Payee Record">
                  Duplicate Payee Record
                </option>
                <option value="Pending Review">Pending Review</option>
                <option value="On Hold">On Hold</option>
                <option value="Mis-Matched Contact Info">
                  Mis-Matched Contact Info
                </option>
              </TextField>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filterActive === true}
                    indeterminate={filterActive === null}
                    onChange={() => {
                      setFilterActive(
                        filterActive === null
                          ? true
                          : filterActive === true
                          ? false
                          : null
                      );
                    }}
                  />
                }
                label="Active"
              />
              <Button
                variant="outlined"
                color="primary"
                onClick={resetFilters}
                sx={{ alignSelf: "flex-start" }}
              >
                Reset Filters
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Box
        mb={2}
        sx={{
          display: "flex",
          gap: 2,
          alignItems: "flex-start",
          flexWrap: "wrap",
        }}
      >
        <FormControlLabel
          control={<Checkbox checked={selectAll} onChange={handleSelectAll} />}
          label="Select All"
        />
        {selectedItems.length > 0 && (
          <>
            <TextField
              label="Memo for Mass Update"
              value={massMemo}
              onChange={(e) => setMassMemo(e.target.value)}
              variant="outlined"
              size="small"
              placeholder="Enter memo (min 7 chars)..."
              aria-label="Memo for mass update"
              multiline
              maxRows={2}
              sx={{ minWidth: 250, flexGrow: 1, maxWidth: 400 }}
              error={massMemo.length > 0 && massMemo.length < 7}
            />
            <Select
              value={massReviewedResults}
              onChange={(e) => setMassReviewedResults(e.target.value)}
              displayEmpty
              size="small"
              sx={{ minWidth: 200 }}
              aria-label="Review status for mass update"
            >
              <MenuItem value="" disabled>
                Select review status...
              </MenuItem>
              <MenuItem value="No Further Action">No Further Action</MenuItem>
              <MenuItem value="Need Additional Information">
                Need Additional Information
              </MenuItem>
              <MenuItem value="Waiting on Applicant">
                Waiting on Applicant
              </MenuItem>
              <MenuItem value="Duplicate Payee Record">
                Duplicate Payee Record
              </MenuItem>
              <MenuItem value="Pending Review">Pending Review</MenuItem>
              <MenuItem value="On Hold">On Hold</MenuItem>
              <MenuItem value="Mis-Matched Contact Info">
                Mis-Matched Contact Info
              </MenuItem>
            </Select>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleMassUpdateActive(true)}
              disabled={
                selectedItems.length === 0 ||
                massMemo.length < 7 ||
                !massReviewedResults ||
                massUpdateLoading
              }
              sx={{ alignSelf: "flex-start" }}
            >
              {massUpdateLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Mark Selected Active"
              )}
            </Button>
            <Button
              variant="contained"
              color="inherit"
              onClick={() => handleMassUpdateActive(false)}
              disabled={
                selectedItems.length === 0 ||
                massMemo.length < 7 ||
                !massReviewedResults ||
                massUpdateLoading
              }
              sx={{ alignSelf: "flex-start" }}
            >
              {massUpdateLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Mark Selected Inactive"
              )}
            </Button>
          </>
        )}
      </Box>
      <Divider my={4} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          {loading && <LoadingSpinner fullScreen={false} />}
          {error && (
            <Typography color="error" variant="body1" mb={2}>
              {error}
            </Typography>
          )}
          {!loading && !error && data.length === 0 && (
            <Typography variant="body1" mb={2}>
              No data available. Total records: {totalCount}
            </Typography>
          )}
          {!loading &&
            !error &&
            data.length > 0 &&
            data.map((item, index) => (
              <StagingItem
                key={item.id || `item-${index}`}
                item={item}
                index={index}
                onTinClick={handleTinClick}
                onPayeeIdClick={handlePayeeIdClick}
                onMainPayeeIdClick={handleMainPayeeIdClick}
                isSelected={selectedItems.includes(item.id)}
                onSelect={(id) => {
                  setSelectedItems((prev) =>
                    prev.includes(id)
                      ? prev.filter((itemId) => itemId !== id)
                      : [...prev, id]
                  );
                }}
              />
            ))}
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={rowsPerPageOptions}
            onRowsPerPageChange={handleChangeRowsPerPage}
            showFirstButton
            showLastButton
            sx={{
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                {
                  color: "text.secondary",
                },
              "& .MuiTablePagination-actions button": {
                borderRadius: "8px",
              },
              "& .MuiTablePagination-select": {
                maxWidth: "120px",
              },
            }}
            labelDisplayedRows={({ from, to, count }) => {
              if (count === 0) return `0–0 of ${count}`;
              if (rowsPerPage === -1 || to >= count)
                return `1–${count} of ${count}`;
              return `${from}–${to} of ${count}`;
            }}
          />
        </Grid>
      </Grid>
      <PayeeModal
        item={selectedPayeeItem}
        open={!!selectedPayeeItem}
        onClose={() => setSelectedPayeeItem(null)}
      />
      <TinMatchModal
        tin={tinMatchModalTin || ""}
        items={data}
        open={!!tinMatchModalTin}
        onClose={() => setTinMatchModalTin(null)}
        onPayeeIdClick={handlePayeeIdClick}
      />
      <PayeeDetailModal
        payeeId={payeeDetailModalId}
        open={!!payeeDetailModalId}
        onClose={() => setPayeeDetailModalId(null)}
        onBillRefCodeClick={handleBillRefCodeClick}
      />
      <BillDetailModal
        billRefCode={billDetailModalRefCode}
        open={!!billDetailModalRefCode}
        onClose={() => setBillDetailModalRefCode(null)}
      />
    </Paper>
  );
}

export default CSITipaltiStagingList;
